import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, map } from 'rxjs';

import { RouterFacade } from '@core/shared/data-access';
import { Pagination, PaginationMetadata } from '@core/shared/domain';
import { LoadingStatus } from '@mp/shared/domain';

import {
  AddArticleToFavoritesList,
  CreateFavoritesList,
  FavoritesList,
  FavoritesListArticle,
  UpdateFavoritesList,
} from '../../common';

import { FavoritesActions, FavoritesMenuActions } from './favorites.actions';
import {
  selectFavoriteListArticles,
  selectFavoriteListArticlesPaginationMetadata,
  selectFavoriteListArticlesStatus,
  selectFavoritesLists,
  selectFavoritesListsStatus,
} from './favorites.selectors';

@Injectable()
export class FavoritesFacade {
  private readonly favoritesStatus$: Observable<LoadingStatus> = this.store$.select(selectFavoritesListsStatus);

  readonly favoritesListsLoading$: Observable<boolean> = this.favoritesStatus$.pipe(
    map((status) => status === LoadingStatus.Loading),
  );

  readonly favoritesLists$: Observable<Array<FavoritesList>> = this.store$.select(selectFavoritesLists);

  readonly favoriteArticles$: Observable<FavoritesListArticle[]> = this.store$.select(selectFavoriteListArticles);

  readonly pagination$: Observable<PaginationMetadata | undefined> = this.store$.select(
    selectFavoriteListArticlesPaginationMetadata,
  );

  private readonly favoriteArticlesStatus$: Observable<LoadingStatus> = this.store$.select(
    selectFavoriteListArticlesStatus,
  );

  readonly favoriteListArticlesLoading$: Observable<boolean> = this.favoriteArticlesStatus$.pipe(
    map((status) => status === LoadingStatus.Loading),
  );

  readonly favoriteListDeleteSuccess$: Observable<string> = this.actions$.pipe(
    ofType(FavoritesActions.deleteFavoritesListSuccess),
    map(({ listId }) => listId),
  );

  readonly fetchFavoritesListsSuccess$: Observable<FavoritesList[]> = this.actions$.pipe(
    ofType(FavoritesActions.fetchFavoritesListsSuccess),
    map(({ favoritesLists }) => favoritesLists),
  );

  constructor(
    private readonly store$: Store,
    private readonly routerFacade: RouterFacade,
    private readonly actions$: Actions,
  ) {}

  fetchFavoritesLists(): void {
    this.store$.dispatch(FavoritesActions.fetchFavoritesLists());
  }

  createFavoritesList(createDto: CreateFavoritesList): void {
    this.store$.dispatch(FavoritesActions.createFavoritesList({ createDto }));
  }

  updateFavoritesList(listId: string, updateDto: UpdateFavoritesList): void {
    this.store$.dispatch(FavoritesActions.updateFavoritesList({ listId, updateDto }));
  }

  onFavoritesListUpdate(updatedList: FavoritesList): void {
    this.store$.dispatch(FavoritesActions.favoritesListUpdate({ updatedList }));
  }

  deleteFavoritesList(listId: string): void {
    this.store$.dispatch(FavoritesActions.deleteFavoritesList({ listId }));
  }

  fetchFavoriteArticles(listId: string, pagination?: Pagination): void {
    this.store$.dispatch(FavoritesActions.fetchFavoriteListArticles({ listId, pagination }));
  }

  clearFavoriteArticles(): void {
    this.store$.dispatch(FavoritesActions.favoriteListArticlesClear());
  }

  addArticleToMenuList(addToListDto: AddArticleToFavoritesList): void {
    this.store$.dispatch(FavoritesMenuActions.addFavoriteListArticle({ addToListDto }));
  }

  removeArticleFromList(listId: string, globalArticleId: string): void {
    this.store$.dispatch(FavoritesActions.deleteFavoriteListArticle({ listId, globalArticleId }));
  }

  removeArticleFromMenuList(listId: string, globalArticleId: string): void {
    this.store$.dispatch(FavoritesMenuActions.deleteFavoriteListArticle({ listId, globalArticleId }));
  }

  navigateBack(): void {
    this.routerFacade.back();
  }
}
